import React from 'react';
import {
    PaperClipOutlined,
    EditOutlined,
    DeleteOutlined,
    EllipsisOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
    RiseOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import Markdown from '@/components/ui/Markdown';
import StatusBadge from '@/components/ui/StatusBadge';
import {
    TaskCardSurface,
    TaskCardTopMetaRow,
    TaskCardTaskIdLabel,
    TaskCardHeadingTitle,
    TaskCardDescScroll,
    CardDescriptionInner,
    MutedText,
    AvatarStackWrap,
    AvatarStackCircle,
    AvatarStackOverflow,
} from '@/pages/inside/tenant/style';
import {
    getIssueOrUpgradeDisplayId,
    getIssueOrUpgradeMarkdown,
    getIssueOrUpgradeType,
    getIssueUpgradeLinkedTasksCount,
    formatIssueUpgradeDateTime,
} from '@hooks/projects/useProjectIssuesAndUpgrades';
import { getInitials, getAvatarColor } from '@hooks/projects/projectHelpers';

export function getIssueUpgradeAttachmentItems(item) {
    const list = [
        ...(Array.isArray(item?.attachments) ? item.attachments : []),
        ...(Array.isArray(item?.attachmentUrls) ? item.attachmentUrls : []),
        ...(Array.isArray(item?.files)
            ? item.files
                .map((f) => ({
                    type: 'file',
                    url: f?.url || f?.downloadUrl || f?.path || '',
                    title: f?.name || f?.filename || 'file',
                }))
                .filter((x) => x.url)
            : []),
    ];
    // Some APIs return placeholder objects; only count real attachment URLs.
    return list.filter((x) => x && typeof x === 'object' && String(x.url ?? '').trim());
}

const attachmentChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 8,
    border: '1px solid var(--ant-color-border-secondary, #e5e7eb)',
    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.65))',
    flexShrink: 0,
};

/** Bottom-left: linked sprint task count — high contrast so it reads on kanban column tints. */
const linkedTaskBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    minWidth: 26,
    height: 24,
    padding: '0 9px',
    boxSizing: 'border-box',
    borderRadius: 999,
    border: '1px solid color-mix(in srgb, var(--ant-color-primary, #1677ff) 38%, transparent)',
    background: 'var(--ant-color-primary-bg, rgba(22, 119, 255, 0.14))',
    color: 'var(--ant-color-primary, #1677ff)',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
};

const HEADER_TOOL_HIT = 28;

/** Issue / upgrade type icon — same icon-only style as Sprint task card work-type icon. */
function IssueUpgradeTypeIcon({ type, typeLabel }) {
    const box = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: HEADER_TOOL_HIT,
        height: HEADER_TOOL_HIT,
        flexShrink: 0,
        lineHeight: 1,
    };
    if (type === 'upgrade') {
        return (
            <Tooltip title={typeLabel}>
                <span role="img" aria-label={typeLabel} style={box}>
                    <RiseOutlined style={{ fontSize: 18, color: '#1677ff' }} aria-hidden />
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={typeLabel}>
            <span role="img" aria-label={typeLabel} style={box}>
                <ExclamationCircleOutlined style={{ fontSize: 18, color: '#E57373' }} aria-hidden />
            </span>
        </Tooltip>
    );
}

/** ⋯ trigger: plain dots, no container; hover turns dots primary (matches Sprint task card). */
function IssueUpgradeOverflowMenuTrigger({ menu, disabled }) {
    const [hover, setHover] = React.useState(false);
    const baseColor = 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))';
    const hoverColor = 'var(--ant-color-primary, #1677ff)';
    return (
        <Dropdown trigger={['click']} placement="bottomRight" menu={menu} disabled={disabled}>
            <button
                type="button"
                aria-label="Card actions"
                disabled={disabled}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: HEADER_TOOL_HIT,
                    height: HEADER_TOOL_HIT,
                    flexShrink: 0,
                    color: hover && !disabled ? hoverColor : baseColor,
                    borderRadius: 0,
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <EllipsisOutlined
                    style={{ fontSize: 18, color: 'currentColor', transform: 'rotate(90deg)' }}
                    aria-hidden
                />
            </button>
        </Dropdown>
    );
}

export default function IssueUpgradeCard({
    item,
    onSelect,
    canEdit = false,
    canDelete = false,
    onEditClick,
    onDeleteClick,
    canCreateProjectTask = false,
    onCreateProjectTaskClick,
    deletePending = false,
    /** When true (e.g. drag overlay), omit the overflow menu */
    suppressCardMenu = false,
    /** ⋯ actions (create task / edit / delete) only on Active column when false */
    showKanbanOverflowMenu = true,
}) {
    const attachmentCount = getIssueUpgradeAttachmentItems(item).length;
    const linkedTaskCount = getIssueUpgradeLinkedTasksCount(item);
    const type = getIssueOrUpgradeType(item);
    const title = String(item?.title || '').trim() || (type === 'upgrade' ? 'Upgrade' : 'Issue');
    const markdown = getIssueOrUpgradeMarkdown(item);
    const reason = String(item?.reason || '').trim();
    const classificationState = String(item?.state || '').trim().toLowerCase();
    const hasClassificationBadge = classificationState === 'genuine' || classificationState === 'improper';
    const typeLabel = type === 'upgrade' ? 'Upgrade' : 'Issue';

    const overflowMenuItems = React.useMemo(() => {
        const items = [];
        if (canCreateProjectTask) {
            items.push({
                key: 'create-task',
                icon: <PlusOutlined />,
                label: 'Create task',
                disabled: deletePending,
            });
        }
        if (canEdit) {
            items.push({
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit',
                disabled: deletePending,
            });
        }
        if (canDelete) {
            items.push({
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                disabled: deletePending,
                danger: true,
            });
        }
        return items;
    }, [canCreateProjectTask, canEdit, canDelete, deletePending]);

    const showOverflowMenu = Boolean(showKanbanOverflowMenu && !suppressCardMenu && overflowMenuItems.length > 0);

    const submittedAtLabel = formatIssueUpgradeDateTime(item?.submittedAt);
    const createdAtLabel = formatIssueUpgradeDateTime(item?.createdAt);

    const actorStack = React.useMemo(() => {
        const created = item?.createdBy && typeof item.createdBy === 'object' ? item.createdBy : null;
        const submitted = item?.submittedBy && typeof item.submittedBy === 'object' ? item.submittedBy : null;
        const createdKey = String(created?.id ?? created?.email ?? '').trim();
        const submittedKey = String(submitted?.id ?? submitted?.email ?? '').trim();
        const samePerson = Boolean(createdKey && submittedKey && createdKey === submittedKey);

        if (samePerson && created) {
            return [
                {
                    key: 'same',
                    user: created,
                    tooltipTitle: (
                        <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                            <div style={{ fontWeight: 700 }}>
                                {created?.name || created?.email || '—'}
                            </div>
                            Created & submitted by
                            <div style={{ marginTop: 4, opacity: 0.95 }}>Created · {createdAtLabel}</div>
                            {item?.submittedAt ? (
                                <div style={{ opacity: 0.95 }}>Submitted · {submittedAtLabel}</div>
                            ) : null}
                        </div>
                    ),
                },
            ];
        }

        const out = [];
        if (created?.id || created?.name || created?.email) {
            out.push({
                key: `created-${createdKey || 'anon'}`,
                user: created,
                tooltipTitle: (
                    <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                        <div style={{ fontWeight: 700 }}>{created?.name || created?.email || '—'}</div>
                        <div style={{ opacity: 0.9 }}>Created by</div>
                        <div style={{ marginTop: 4, opacity: 0.95 }}>{createdAtLabel}</div>
                    </div>
                ),
            });
        }
        if (submitted?.id || submitted?.name || submitted?.email) {
            out.push({
                key: `submitted-${submittedKey || 'anon'}`,
                user: submitted,
                tooltipTitle: (
                    <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                        <div style={{ fontWeight: 700 }}>{submitted?.name || submitted?.email || '—'}</div>
                        <div style={{ opacity: 0.9 }}>Submitted by</div>
                        <div style={{ marginTop: 4, opacity: 0.95 }}>{submittedAtLabel}</div>
                    </div>
                ),
            });
        }
        if (out.length === 0 && (submitted?.id || submitted?.name || submitted?.email)) {
            out.push({
                key: `submitted-${submittedKey || 'anon'}`,
                user: submitted,
                tooltipTitle: (
                    <div style={{ fontSize: 12, lineHeight: 1.45 }}>
                        <div style={{ fontWeight: 700 }}>{submitted?.name || submitted?.email || '—'}</div>
                        <div style={{ opacity: 0.9 }}>Submitted by</div>
                        <div style={{ marginTop: 4, opacity: 0.95 }}>{submittedAtLabel}</div>
                    </div>
                ),
            });
        }
        return out;
    }, [item, createdAtLabel, submittedAtLabel]);

    const hasDateOrActors = Boolean(
        item?.createdAt ||
        item?.submittedAt ||
        actorStack.length > 0 ||
        (item?.createdBy && typeof item.createdBy === 'object') ||
        (item?.submittedBy && typeof item.submittedBy === 'object'),
    );
    const showFooter = attachmentCount > 0 || linkedTaskCount > 0 || hasDateOrActors;

    return (
        <TaskCardSurface
            $kanbanGrab
            $kanbanMuteHover
            $kanbanColumn="todo"
            onClick={onSelect ? () => onSelect(item) : undefined}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}
        >
            <TaskCardTopMetaRow style={{ marginBottom: 6, justifyContent: 'space-between', alignItems: 'center' }}>
                <TaskCardTaskIdLabel style={{ flexShrink: 0 }}>{getIssueOrUpgradeDisplayId(item)}</TaskCardTaskIdLabel>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <IssueUpgradeTypeIcon type={type} typeLabel={typeLabel} />
                    {hasClassificationBadge ? (
                        <StatusBadge status={classificationState} size="small" />
                    ) : null}
                    {showOverflowMenu ? (
                        <IssueUpgradeOverflowMenuTrigger
                            disabled={deletePending}
                            menu={{
                                items: overflowMenuItems,
                                onClick: ({ key, domEvent }) => {
                                    domEvent?.stopPropagation?.();
                                    if (key === 'create-task') {
                                        if (!canCreateProjectTask || deletePending) return;
                                        onCreateProjectTaskClick?.(item);
                                        return;
                                    }
                                    if (key === 'edit') {
                                        if (!canEdit || deletePending) return;
                                        onEditClick?.(item);
                                        return;
                                    }
                                    if (key === 'delete') {
                                        if (!canDelete || deletePending) return;
                                        onDeleteClick?.(item);
                                    }
                                },
                            }}
                        />
                    ) : null}
                </div>
            </TaskCardTopMetaRow>
            <TaskCardHeadingTitle $lineClamp1 style={{ marginBottom: 6 }}>
                {title}
            </TaskCardHeadingTitle>
            {markdown ? (
                <TaskCardDescScroll $kanbanColumn="todo" $previewLines={10}>
                    <Markdown
                        source={markdown}
                        component={CardDescriptionInner}
                        variant="compact"
                        lineClamp={null}
                        linkPrimary
                        richInlineAnchors
                        attachmentItems={getIssueUpgradeAttachmentItems(item)}
                    />
                </TaskCardDescScroll>
            ) : (
                <MutedText style={{ fontSize: 12, margin: 0 }}>No description.</MutedText>
            )}
            {reason ? (
                <div
                    style={{
                        marginTop: 8,
                        marginBottom: 8,
                        paddingTop: 8,
                        paddingBottom: 8,
                        borderTop: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.12))',
                        borderBottom: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.12))',
                    }}
                >
                    <MutedText style={{ fontSize: 11, margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Reason
                    </MutedText>
                    <TaskCardDescScroll $kanbanColumn="todo" $previewLines={3} style={{ marginTop: 0 }}>
                        <Markdown source={reason} component={CardDescriptionInner} variant="compact" lineClamp={3} />
                    </TaskCardDescScroll>
                </div>
            ) : null}
            {showFooter ? (
                <div
                    data-kanban-issue-upgrade-card-footer
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                        flexWrap: 'wrap',
                        marginTop: 0,
                        paddingTop: 2,
                        minHeight: 0,
                        flexShrink: 0,
                        borderTop: '1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.08))',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            minHeight: 32,
                            minWidth: 0,
                            flex: '1 1 auto',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Tooltip
                            title={`${linkedTaskCount} linked sprint task${linkedTaskCount === 1 ? '' : 's'}`}
                            mouseEnterDelay={0.35}
                        >
                            <span
                                style={{
                                    ...linkedTaskBadgeStyle,
                                    opacity: linkedTaskCount > 0 ? 1 : 0.55,
                                    filter: linkedTaskCount > 0 ? 'none' : 'grayscale(0.25)',
                                }}
                                aria-label={`${linkedTaskCount} linked tasks`}
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                role="status"
                            >
                                <UnorderedListOutlined style={{ fontSize: 13 }} aria-hidden />
                                <span>{linkedTaskCount}</span>
                            </span>
                        </Tooltip>
                        <span
                            style={{
                                ...attachmentChipStyle,
                                opacity: attachmentCount > 0 ? 1 : 0.55,
                                pointerEvents: 'none',
                            }}
                            aria-hidden
                        >
                            <PaperClipOutlined style={{ fontSize: 12 }} />
                            {attachmentCount}
                        </span>
                    </div>
                    <div
                        style={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 8,
                            flexShrink: 1,
                            minWidth: 0,
                            maxWidth: '100%',
                        }}
                    >

                        {item?.submittedAt ? (
                            <MutedText
                                style={{
                                    fontSize: 11,
                                    margin: 0,
                                    lineHeight: 1.35,
                                    opacity: 0.88,
                                    textAlign: 'right',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {submittedAtLabel}
                            </MutedText>
                        ) : item?.createdAt ? (
                            <MutedText
                                style={{
                                    fontSize: 11,
                                    margin: 0,
                                    lineHeight: 1.35,
                                    opacity: 0.88,
                                    textAlign: 'right',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {createdAtLabel}
                            </MutedText>
                        ) : null}
                        {actorStack.length > 0 ? (
                            <AvatarStackWrap style={{ flexShrink: 0 }}>
                                {actorStack.slice(0, 2).map((entry) => {
                                    const u = entry.user || {};
                                    const name = u?.name || u?.email || '—';
                                    const initialsKey = String(u?.id ?? u?.email ?? name);
                                    return (
                                        <Tooltip key={entry.key} title={entry.tooltipTitle} mouseEnterDelay={0.35}>
                                            <AvatarStackCircle $bg={getAvatarColor(initialsKey)} $color="#fff">
                                                {getInitials(name)}
                                            </AvatarStackCircle>
                                        </Tooltip>
                                    );
                                })}
                                {actorStack.length > 2 ? (
                                    <Tooltip title={`${actorStack.length - 2} more`}>
                                        <AvatarStackOverflow>+{actorStack.length - 2}</AvatarStackOverflow>
                                    </Tooltip>
                                ) : null}
                            </AvatarStackWrap>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </TaskCardSurface>
    );
}
